/**
 * Minimal cookie helpers replacing js-cookie. Reads and writes use the same
 * encoding as js-cookie v3, so cookies written by earlier versions of the
 * tag read back unchanged and new writes are byte-identical.
 */

export interface CookieAttributes {
  /** Days until the cookie expires. Omit for a session cookie. */
  expires?: number;
  domain?: string;
}

function readValue(value: string): string {
  if (value[0] === '"') value = value.slice(1, -1);
  return value.replace(/(%[\dA-F]{2})+/gi, decodeURIComponent);
}

function writeValue(value: string): string {
  return encodeURIComponent(value).replace(
    /%(2[346BF]|3[AC-F]|40|5[BDE]|60|7[BCD])/g,
    decodeURIComponent
  );
}

function parseCookies(name?: string): Record<string, string> {
  const jar: Record<string, string> = {};
  const cookies = document.cookie ? document.cookie.split("; ") : [];
  for (const cookie of cookies) {
    const separator = cookie.indexOf("=");
    const rawName = separator < 0 ? cookie : cookie.slice(0, separator);
    const rawValue = separator < 0 ? "" : cookie.slice(separator + 1);
    try {
      const found = decodeURIComponent(rawName);
      jar[found] = readValue(rawValue);
      // The first match wins, as the browser lists the most specific first.
      if (found === name) break;
    } catch {
      // skip cookies that are not valid URI-encoded text
    }
  }
  return jar;
}

/**
 * Returns the value of the named cookie, or undefined if it is not set.
 */
export function getCookie(name: string): string | undefined {
  return parseCookies(name)[name];
}

/**
 * Returns every cookie visible to the page, keyed by name.
 */
export function getAllCookies(): Record<string, string> {
  return parseCookies();
}

/**
 * Sets a cookie on path `/`.
 */
export function setCookie(
  name: string,
  value: string,
  { expires, domain }: CookieAttributes = {}
): void {
  let cookie = `${encodeURIComponent(name)}=${writeValue(value)}; path=/`;
  if (expires != null) {
    cookie += `; expires=${new Date(Date.now() + expires * 864e5).toUTCString()}`;
  }
  if (domain) cookie += `; domain=${domain}`;
  document.cookie = cookie;
}

/**
 * Removes a cookie. `domain` must match the one it was set with.
 */
export function removeCookie(
  name: string,
  { domain }: CookieAttributes = {}
): void {
  setCookie(name, "", { expires: -1, domain });
}
